import { AppUser, getObjectById, IEventrParking, IEventrParkingCar, SectionId } from "@eventr/common";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useBoundStore } from "state/bound.store";
import { IItinerary } from "state/interfaces";
import { RocketChannel } from "state/rocketchat/rocketchat.interface";
import { coverImageUrl } from "feat/media/cover-image";

export interface ResolvedChatIdentity {
  name: string;
  /** Photo to show for this chat, when one can be resolved; absent falls back to the
   * generic chat-type icon. */
  photoUrl?: string;
}

interface ChatIdentityLookupDeps {
  itineraryMap: { id: number; itinerary_name: string }[];
  selectedItinID: number;
  selectedItinerary: IItinerary | undefined;
}

type IdentityResolver = (channel: RocketChannel, deps: ChatIdentityLookupDeps) => ResolvedChatIdentity | undefined;

/** section_id -> { array key on the section's data, title field on each item }. */
const FLAT_ITEM_LOOKUPS: Partial<Record<number, { dataKey: string; titleField: string }>> = {
  [SectionId.FLIGHT]: { dataKey: "flights", titleField: "bookref" },
  [SectionId.HOTEL]: { dataKey: "hotels", titleField: "name" },
  [SectionId.HIRE_CAR]: { dataKey: "hireCars", titleField: "car_alias" },
  [SectionId.TRANSPORT]: { dataKey: "transport", titleField: "transport_alias" },
  [SectionId.ACTIVITY]: { dataKey: "activities", titleField: "activity_name" },
};

/** Live identity for the whole-itinerary chat: the itinerary's real name and cover photo. */
function resolveItineraryChatIdentity(
  channel: RocketChannel,
  { itineraryMap, selectedItinID }: ChatIdentityLookupDeps,
): ResolvedChatIdentity | undefined {
  if (channel.itinerary_id !== selectedItinID) return undefined;

  const itin = itineraryMap.find((i) => i.id === channel.itinerary_id);
  if (!itin?.itinerary_name) return undefined;

  return {
    name: itin.itinerary_name,
    photoUrl: coverImageUrl(useBoundStore.getState().getEventInfoV2()?.data, "tiny"),
  };
}

/** Live identity for a flat per-item chat (flight/hotel/hire car/transport/activity): the
 * item's own title field. No photo — these keep their generic type icon. */
function resolveFlatItemChatIdentity(
  channel: RocketChannel,
  { selectedItinID, selectedItinerary }: ChatIdentityLookupDeps,
  { dataKey, titleField }: { dataKey: string; titleField: string },
): ResolvedChatIdentity | undefined {
  if (channel.itinerary_id !== selectedItinID || !channel.object_id) return undefined;

  const section = selectedItinerary?.v2?.find((s) => s.sectionID === channel.section_id);
  const items = (section?.data as Record<string, unknown[]> | undefined)?.[dataKey] ?? [];
  const item = getObjectById(channel.object_id, items) as Record<string, string> | undefined;
  const title = item?.[titleField];

  return title ? { name: title } : undefined;
}

/** Live identity for a parking chat: object_id is the car's own id, not the booking's, so
 * every booking's cars are searched for the match. Title combines the booking's name with
 * that car's plate. No photo — parking chats keep their generic type icon. */
function resolveParkingChatIdentity(
  channel: RocketChannel,
  { selectedItinID, selectedItinerary }: ChatIdentityLookupDeps,
): ResolvedChatIdentity | undefined {
  if (channel.itinerary_id !== selectedItinID || !channel.object_id) return undefined;

  const section = selectedItinerary?.v2?.find((s) => s.sectionID === SectionId.PARKING);
  const parkings = (section?.data?.parkings ?? []) as IEventrParking[];

  for (const parking of parkings) {
    const car = getObjectById(channel.object_id, parking.cars ?? []) as IEventrParkingCar | undefined;
    if (car) {
      if (!parking.parking_name || !car.number_plate) return undefined;
      return { name: `${parking.parking_name} ${car.number_plate}` };
    }
  }

  return undefined;
}

/** Live resolvers for chat types with resolvable item data, keyed by section_id. */
const liveIdentityResolvers: Partial<Record<number, IdentityResolver>> = {
  [SectionId.GENERAL]: resolveItineraryChatIdentity,
  [SectionId.PARKING]: resolveParkingChatIdentity,
  ...Object.fromEntries(
    Object.entries(FLAT_ITEM_LOOKUPS).map(([sectionId, lookup]) => [
      sectionId,
      (channel: RocketChannel, deps: ChatIdentityLookupDeps) => resolveFlatItemChatIdentity(channel, deps, lookup!),
    ]),
  ),
};

/** A person's name as the app shows it: first and last name. Empty when the user has neither. */
export const appUserFullName = (user: Pick<AppUser, "firstName" | "lastName">): string =>
  [user.firstName, user.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

function genericChatLabel(sectionId: number): string {
  switch (sectionId) {
    case SectionId.GENERAL:
      return "Itinerary chat";
    case SectionId.FLIGHT:
      return "Flight chat";
    case SectionId.HOTEL:
      return "Accomodation chat";
    case SectionId.HIRE_CAR:
      return "Hire car chat";
    case SectionId.TRANSPORT:
      return "Transport chat";
    case SectionId.ACTIVITY:
      return "Activity chat";
    case SectionId.PARKING:
      return "Parking chat";
    default:
      return "Group chat";
  }
}

/** Resolves a channel's display identity: live resolver for its type > name stored at
 * creation time > generic label. Direct chats resolve to the other user's name, no photo. */
function resolveChatIdentity(
  channel: RocketChannel | null | undefined,
  users: AppUser[] | undefined,
  user: AppUser | undefined,
  deps: ChatIdentityLookupDeps,
): ResolvedChatIdentity {
  if (!channel) return { name: "Chat" };

  if (channel.type === "direct") {
    const otherUserId = channel.members?.find((u) => u !== user?.id);
    const otherUser = users?.find((u) => u.id === otherUserId);
    return { name: (otherUser && appUserFullName(otherUser)) || "Direct chat" };
  }

  const liveIdentity = liveIdentityResolvers[channel.section_id]?.(channel, deps);
  if (liveIdentity) return liveIdentity;

  if (channel.json_data?.channel_name_parts && channel.json_data.channel_name_parts.length > 0) {
    return { name: channel.json_data.channel_name_parts.map((c) => c.replaceAll("_", " ")).join(" ") };
  } else if (channel.json_data?.channel_name && channel.json_data.channel_name.length > 0) {
    return { name: channel.json_data.channel_name };
  } else {
    return { name: genericChatLabel(channel.section_id) };
  }
}

export const useChatIdentity = (rcChannelID: string): ResolvedChatIdentity => {
  const { channels, users, user, itineraryMap, selectedItinID, selectedItinerary } = useBoundStore(
    useShallow((s) => {
      return {
        users: s.users,
        channels: s.rocketchannels[s.selectedItinID],
        user: s.wpUser,
        itineraryMap: s.itineraryMap,
        selectedItinID: s.selectedItinID,
        selectedItinerary: s.selectedItinerary,
      };
    }),
  );
  const channel = useMemo(() => {
    if (!rcChannelID) return null;
    const channel = channels?.find((c) => c.channel_id === rcChannelID);
    if (!channel) return null;
    return channel;
  }, [rcChannelID, channels]);

  return useMemo(
    () => resolveChatIdentity(channel, users, user, { itineraryMap, selectedItinID, selectedItinerary }),
    [channel, users, user, itineraryMap, selectedItinID, selectedItinerary],
  );
};

export const useGetChatName = (rcChannelID: string): string => useChatIdentity(rcChannelID).name;

/**
 * Non-hook equivalent of useChatIdentity. Reads state via getState() rather than
 * subscribing, so it won't reflect later updates to channels/users/wpUser/itineraryMap.
 */
export function getChatIdentitySnapshot(rcChannelID: string): ResolvedChatIdentity {
  const state = useBoundStore.getState();
  const channels = state.rocketchannels[state.selectedItinID];
  const channel = rcChannelID ? (channels?.find((c) => c.channel_id === rcChannelID) ?? null) : null;
  return resolveChatIdentity(channel, state.users, state.wpUser, {
    itineraryMap: state.itineraryMap,
    selectedItinID: state.selectedItinID,
    selectedItinerary: state.selectedItinerary,
  });
}

/**
 * Non-hook equivalent of useGetChatName. Reads state via getState() rather than subscribing,
 * so it won't reflect later updates to channels/users/wpUser.
 */
export function getChatNameSnapshot(rcChannelID: string): string {
  return getChatIdentitySnapshot(rcChannelID).name;
}
